// Signal-backed store for the "My Day" view.
// Follows the lazy-load / refresh pattern established in favorites.ts.

import { signal, type Signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import type { PersonalTask, Task } from "@juuplan/shared";
import { api } from "../lib/api";
import { localTodayIso } from "../lib/format";

// ---------- local composite types ----------

// The API includes `project_id` on `task` (resolved via phases) so items can
// deep-link into the owning project's board — see MyDayPlanItem for the same pattern.
type MyDayTask = Task & { project_id: number | null };

export interface MyDayItem {
  entry_id: number;
  sort_order: number;
  kind: "task" | "personal";
  task?: MyDayTask;
  personal_task?: PersonalTask;
}

export interface MyDaySuggestion {
  kind: "task" | "personal";
  task?: MyDayTask;
  personal_task?: PersonalTask;
}

export interface MyDayResponse {
  day: string;
  items: MyDayItem[];
  suggestions: MyDaySuggestion[];
}

// ---------- state ----------

export interface MyDayState {
  day: string;
  items: MyDayItem[];
  suggestions: MyDaySuggestion[];
  loading: boolean;
}

const defaultDay = localTodayIso();

const myDaySignal: Signal<MyDayState> = signal({
  day: defaultDay,
  items: [],
  suggestions: [],
  loading: true,
});

const daySignal: Signal<string> = signal(defaultDay);
let myDayStarted = false;

async function loadMyDay(): Promise<void> {
  myDaySignal.value = { ...myDaySignal.value, loading: true };
  try {
    const data = await api<MyDayResponse>(`/api/my-day?day=${daySignal.value}`, {
      silentError: true,
    });
    myDaySignal.value = {
      day: data.day,
      items: data.items ?? [],
      suggestions: data.suggestions ?? [],
      loading: false,
    };
  } catch {
    myDaySignal.value = { ...myDaySignal.value, loading: false };
  }
}

export function useMyDay(): MyDayState {
  useEffect(() => {
    if (!myDayStarted) {
      myDayStarted = true;
      void loadMyDay();
    }
  }, []);
  return myDaySignal.value;
}

export function setMyDayDate(day: string): void {
  daySignal.value = day;
  void loadMyDay();
}

export function refreshMyDay(): Promise<void> {
  return loadMyDay();
}

export async function addToMyDay(body: {
  task_id?: number;
  personal_task_id?: number;
  day?: string;
}): Promise<void> {
  await api<unknown>("/api/my-day", { method: "POST", body });
  await loadMyDay();
}

export async function removeFromMyDay(entryId: number): Promise<void> {
  await api<void>(`/api/my-day/${entryId}`, { method: "DELETE" });
  await loadMyDay();
}
